import type {
  ArgsOf,
  RequestOf,
  StagedAccessRequestOf,
} from "@p0security/shared/types/access/index";
import { Access } from "@p0security/shared/types/access/index";
import type { PermissionRequest } from "@p0security/shared/types/permission";

export const AzureRdpAccesses = {
  session: Access({
    description: "RDP session instance",
    label: "RDP session",
    delegation: {},
    generated: {
      principalId: {
        type: "string",
        label: "Principal ID",
        description: "ID of the principal to assign the role to",
      },
      userLoginRoleAssignmentId: {
        type: "string",
        label: "Role assignment ID",
        description: "Assignment ID of role for virtual machine",
      },
      adminUserLoginRoleAssignmentId: {
        type: "string",
        optional: true,
        label: "Role assignment ID",
        description: "Assignment ID of role for virtual machine",
      },
      bastionHostRoleAssignmentId: {
        type: "string",
        label: "Bastion host role assignment ID",
        description: "Assignment ID of role for bastion host",
      },
      networkInterfaceRoleAssignmentId: {
        type: "string",
        label: "Network interface role assignment ID",
        description: "Assignment ID of role for network interface",
      },
    },
    maturity: "beta",
    parameters: {
      destination: {
        type: "dynamic",
        label: "Destination",
        description: "The destination of the RDP session",
      },
      admin: {
        type: "boolean",
        label: "Admin",
        description: "Whether to use admin credentials",
        valueLabel: "Admin",
        optional: true,
      },
      // Azure RDP is only supported through an Azure Bastion host (enforced at
      // install time). The connection point gets the standard access role; the
      // target VM gets the built-in Windows login roles.
      bastionHost: {
        type: "object",
        label: "Bastion host",
        properties: {
          id: {
            type: "generated",
            label: "Bastion host ID",
          },
          roleId: {
            type: "generated",
            label: "Bastion host role definition ID",
          },
        },
      },
      resource: {
        type: "object",
        label: "Data",
        properties: {
          instanceName: {
            type: "generated",
            label: "Instance name",
          },
          directoryId: {
            type: "generated",
            label: "Directory ID",
          },
          instanceId: {
            type: "generated",
            label: "Resource ID",
          },
          subscriptionId: {
            type: "generated",
            label: "Subscription ID",
          },
          region: {
            type: "generated",
            label: "Region",
          },
          networkInterface: {
            type: "object",
            label: "Network interface",
            properties: {
              id: {
                type: "generated",
                label: "Network interface ID",
              },
              subnetId: {
                type: "generated",
                label: "Subnet ID",
              },
            },
          },
        },
      },
    },
    render: { locator: "destination" } as const,
  }),
};

export type AzureRdpAccesses = typeof AzureRdpAccesses;

export type AzureRdpSessionAccess = AzureRdpAccesses["session"];
export type AzureRdpSessionAccessArgs = ArgsOf<AzureRdpSessionAccess>;
export type AzureRdpSessionRequest = RequestOf<AzureRdpSessionAccess>;

export const isAzureRdpSessionRequest = (
  request: PermissionRequest
): request is StagedAccessRequestOf<typeof AzureRdpAccesses, "session"> & {
  type: "rdp";
} => request.type === "rdp" && request.access === "session";
